import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import IndicadorProhibido, { tituloProhibido } from '../IndicadorProhibido';
import {
  toMin, toHHMM, strToDate, todayStr, conceptColor, setEstadoCita, consultaConjuntaDeLaCita,
  esChalazionDeQuirofano, esConsultaEnQuirofano, esOnline, esCitaAnulada,
  ESTADO_RESERVA, colorDeLaReserva, nombreTipoDeLaCita,
} from '../../lib/citasApi';
import { ordenarIntervenciones, textoIntervencion, tituloIntervenciones } from './EtiquetasIntervenciones';
import { EtiquetaMarcaPaciente, marcasDeLaCita, tituloMarca } from './EtiquetasMarcasPaciente';

/**
 * El distintivo ONLINE (dirección, 5/10/2026: «un cartelito tipo online,
 * en amarillo, exactamente igual que el que sale al imprimir el parte»). Los
 * mismos colores que `.online-badge` del parte
 * (services/agenda_dia_pdf_generator.py): amarillo chillón, letras negras,
 * negrita y su borde. No depende del color del tipo de cita.
 */
function EtiquetaOnline({ copia = false }) {
  return (
    <span
      className="inline-flex items-center rounded-[3px] border border-[#E6CE00] bg-[#FFE500] px-[5px] py-0 text-[10px] font-bold tracking-[0.08em] text-black leading-none whitespace-nowrap align-middle"
      data-testid={copia ? undefined : 'cita-online-badge'}
      title={copia ? undefined : 'Consulta online'}
    >
      ONLINE
    </span>
  );
}

/**
 * El distintivo RESERVA (administración 8/10; dirección, 12/10/2026): la cita
 * dada como «Reserva <tipo>» y aún sin pagar. Las citas online ya usan el
 * amarillo como color de tipo, así que lo que la distingue es esta etiqueta
 * (decisión de dirección del 12/10, que sigue el 15/10 con el rectángulo
 * entero en amarillo): el mismo estilo que ONLINE, pero con su texto y el
 * borde NEGRO (el de ONLINE es amarillo oscuro), para que no se confundan una
 * al lado de la otra.
 * `corta`: en una columna estrecha dice «RES.» (el título lo dice entero),
 * para que quepa al lado de ONLINE y de la confirmación.
 * `copia` (los distintivos): la que se mide sin verse, sin testid ni título.
 *
 * Fuera el «€» rojo (15/10/2026): lo pagado lo dice el color del rectángulo,
 * azul o verde (`colorDeLaReserva`), y su título; el azul lleva además
 * RESTO PEND. (`EtiquetaRestoPendiente`), para no confundirlo con un tipo azul.
 */
function EtiquetaReserva({ corta = false, copia = false }) {
  return (
    <span
      className="inline-flex items-center rounded-[3px] border border-black bg-[#FFE500] px-[5px] py-0 text-[10px] font-bold tracking-[0.08em] text-black leading-none whitespace-nowrap align-middle"
      data-testid={copia ? undefined : 'cita-reserva-badge'}
      title={copia ? undefined : 'Reserva sin pagar: queda pagada al apuntar en Cobros el pago que le toca, o al elegir el tipo normal'}
      aria-label={corta && !copia ? 'Reserva sin pagar' : undefined}
    >
      {corta ? 'RES.' : 'RESERVA'}
    </span>
  );
}

/**
 * El distintivo RESTO PEND. (revisión del 15/10/2026): la cirugía con la
 * reserva cobrada y el resto del presupuesto por cobrar. El azul de la cita
 * lo dice, pero un tipo de color propio azul se le parece: la etiqueta, con
 * el mismo estilo que RESERVA en azul, es lo que no se confunde. `corta`:
 * «RESTO» en una columna estrecha (el título lo dice entero).
 */
const TITULO_RESTO_PENDIENTE = 'Reserva cobrada; falta el resto del presupuesto';
function EtiquetaRestoPendiente({ corta = false, copia = false }) {
  return (
    <span
      className="inline-flex items-center rounded-[3px] border border-[#1E3A8A] bg-[#DBEAFE] px-[5px] py-0 text-[10px] font-bold tracking-[0.06em] text-[#1E3A8A] leading-none whitespace-nowrap align-middle"
      data-testid={copia ? undefined : 'cita-resto-pendiente'}
      title={copia ? undefined : TITULO_RESTO_PENDIENTE}
      aria-label={copia ? undefined : TITULO_RESTO_PENDIENTE}
    >
      {corta ? 'RESTO' : 'RESTO PEND.'}
    </span>
  );
}

/**
 * AgendaGrid — primitiva compartida de la vista de agenda (día y semana).
 *
 * Props:
 *  - agenda:      agenda activa ({slot_minutes, working_hours, ...})
 *  - date:        "YYYY-MM-DD" (vista día) — alternativamente…
 *  - dates:       ["YYYY-MM-DD", ...] (vista semana; tiene prioridad sobre date)
 *  - citas:       citas a pintar (de cualquiera de los días visibles)
 *  - bloqueos:    bloqueos de agenda del rango visible
 *  - conceptos:   catálogo de conceptos (para colorear por concept_code)
 *  - onSlotClick: ({date, start}) al pulsar un hueco laborable vacío
 *  - onCitaClick: (cita, event) al pulsar una cita
 *  - compact:     escala reducida (vista semana)
 *  - isAdmin:     permite click fuera de horario/bloqueo (el backend valida con force)
 *  - showHeaders / onDateHeaderClick: cabeceras de columna clicables (semana)
 *  - agendas:     las agendas cargadas en la pantalla; con ellas una cita
 *                 prestada decide su «visto por enfermería» con SU agenda
 *  - ajustarAlAncho: varias agendas a la vez (5/10/2026). La rejilla no
 *                 impone ancho mínimo: ocupa lo que le dé la pantalla y nada
 *                 se sale de lado (sin scroll horizontal).
 *  - sinRegla:    sin la regla de horas de la izquierda, para columnas
 *                 estrechas: la hora va igualmente en cada cita.
 */

const GUTTER_PX = 56;

function hexToRgba(hex, alpha) {
  const h = String(hex || '').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  if (full.length !== 6 || Number.isNaN(n)) return `rgba(46,110,94,${alpha})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

/** Índice de working_hours para una fecha (0 = lunes … 6 = domingo). */
function weekdayKey(dateStr) {
  return String((strToDate(dateStr).getDay() + 6) % 7);
}

function nowMinutes() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

/**
 * Coloca las citas a ANCHO COMPLETO, sin carriles laterales. Si dos citas se
 * solapan en el tiempo, la segunda se APILA justo debajo de la primera en vez
 * de estrecharse a un lado: el día "se expande" hacia abajo lo necesario y
 * todas las citas mantienen el mismo tamaño (2026-07-16, petición de la
 * clínica — al forzar/añadir un hueco nada se mueve a izquierda/derecha).
 *
 * Devuelve cada item con `top`/`height` ya calculados en píxeles.
 */
function layoutCitas(citas, pxPerMin, minStart, stack = true) {
  const items = citas
    .map((c) => {
      const start = toMin(c.start);
      const end = c.end ? toMin(c.end) : start + (c.duration_minutes || 15);
      return { cita: c, start, end: Math.max(end, start + 5), lane: 0, laneCount: 1 };
    })
    .sort((a, b) => a.start - b.start || b.end - a.end);

  if (!stack) {
    // PROPORCIONAL (vista semana / varias columnas): cada cita en su hora real,
    // alineada con la regla de horas fija de la izquierda.
    for (const it of items) {
      it.top = (it.start - minStart) * pxPerMin;
      it.height = Math.max(14, (it.end - it.start) * pxPerMin - 2);
    }
    return items;
  }

  // APILADO UNIFORME (vista de un solo día / agenda suelta): todas las citas del
  // MISMO tamaño mínimo (para que se lean nombre + nota), apiladas como lista.
  // La regla de horas se alinea a cada cita.
  //
  // Los RATOS LIBRES entre dos citas sí se dibujan a escala (2026-07-21), para
  // ver de un vistazo dónde cabe alguien. Se topan por arriba y por abajo: sin
  // suelo un hueco de 15 min no se vería, y sin techo una tarde libre dejaría
  // media pantalla en blanco y habría que hacer scroll para nada.
  const HUECO_MIN_MINUTOS = 10;   // por debajo de esto no merece la pena pintarlo
  const HUECO_PX_MIN = 26;
  const HUECO_PX_MAX = 150;
  const altoHueco = (minutos) =>
    Math.round(Math.min(HUECO_PX_MAX, Math.max(HUECO_PX_MIN, minutos * pxPerMin)));

  // Una cita «no acude» se dibuja en su hueco, tachada (dirección, 2026-09-08,
  // punto 12), pero NO ocupa el tiempo: el rato libre se mide desde la última
  // cita activa, así el hueco se sigue ofreciendo para citar a otro paciente,
  // y encima de la tachada no se pinta ningún «libres» (sería contarlo dos veces).
  let prevBottom = null;
  let prevEnd = null;
  for (const it of items) {
    const h = Math.max(60, (it.end - it.start) * pxPerMin - 2);
    const noAcude = esNoAcude(it.cita);
    let top;
    if (prevBottom == null) {
      top = 0;
    } else {
      const libres = prevEnd != null && !noAcude ? it.start - prevEnd : 0;
      if (libres >= HUECO_MIN_MINUTOS) {
        // La comida sigue siendo un hueco especial (etiqueta y color propios),
        // pero ya no mide 42 px fijos: mide lo que dura, como el resto.
        const esComida = libres >= 60 && prevEnd <= 16 * 60 && it.start >= 13 * 60;
        const px = altoHueco(libres);
        it.huecoArriba = {
          minutos: libres,
          px,
          tipo: esComida ? 'comida' : 'libre',
          desde: prevEnd,
          hasta: it.start,
        };
        top = prevBottom + 2 + px;
      } else {
        top = prevBottom + 2;
      }
    }
    it.top = top;
    it.height = h;
    prevBottom = top + h;
    if (!noAcude) prevEnd = it.end;
  }
  return items;
}

/** Cita a la que el paciente no se presentó (status «no_acude»). */
function esNoAcude(cita) {
  return cita?.status === 'no_acude';
}

/** "45 min" · "1 h" · "1 h 30 min" */
function duracionLegible(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

// Cómo va la etiqueta de cirugía previa con varias agendas a la vez
// (5/10/2026), donde la columna puede ser estrecha: se corta con «…» dentro de
// la cita en vez de salirse por la derecha (el título conserva el texto
// entero). `linea`: en la línea de la hora encoge lo que haga falta. `fila`:
// en la fila de distintivos de una columna estrecha pide al menos 3rem y, si
// no los tiene, pasa a la fila siguiente, que no se ve.
const CLASE_QX_PREVIA = 'rounded px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wide bg-red-600 text-white leading-none';
const CLASE_QX_PREVIA_AJUSTE = {
  linea: `inline-block min-w-0 max-w-full truncate align-middle ${CLASE_QX_PREVIA}`,
  fila: `block min-w-0 basis-12 grow max-w-max truncate ${CLASE_QX_PREVIA}`,
};
const CLASE_QX_PREVIA_SUELTA = `inline-flex items-center align-middle whitespace-nowrap ${CLASE_QX_PREVIA}`;
// El «+N» (14/10/2026): el mismo rojo, y nunca se encoge ni se corta; con él a
// la vista se sabe que hay más.
const CLASE_QX_MAS = 'rounded px-1 py-[1px] text-[10px] font-bold bg-red-600 text-white leading-none whitespace-nowrap';
const CLASE_QX_MAS_AJUSTE = {
  linea: `inline-block shrink-0 align-middle ${CLASE_QX_MAS}`,
  fila: `block shrink-0 ${CLASE_QX_MAS}`,
};

/**
 * LAS MARCAS DE PACIENTE junto al nombre (administración, 21/10/2026): la
 * etiqueta de las intervenciones, en el color de la marca y con el texto que
 * guarda la cita (EtiquetasMarcasPaciente.jsx). El nombre manda: la etiqueta
 * encoge antes que él y se corta con «…» (el título la dice entera), pero
 * nunca desaparece del todo.
 * `ajuste`:
 *  · 'nombre': en la línea del nombre, a su derecha;
 *  · 'fila':   en su propia fila debajo del nombre (columna estrecha, donde
 *              el nombre ocupa hasta dos líneas y no queda sitio al lado);
 *  · 'suelta': seguidas en la línea de la cita corta.
 */
const CLASE_MARCAS_AJUSTE = {
  nombre: 'flex items-center gap-1 min-w-[2.75rem] max-w-[60%] shrink-[6] overflow-hidden',
  fila: 'flex items-center gap-1 min-w-0 overflow-hidden mb-0.5',
};
function MarcasEnLaCita({ marcas, citaId, ajuste = 'nombre' }) {
  if (!marcas.length) return null;
  const etiquetas = marcas.map((m) => (
    <EtiquetaMarcaPaciente
      key={m.id || m.texto}
      texto={m.texto}
      color={m.color}
      className={ajuste === 'suelta' ? 'ml-1 !max-w-[45%]' : 'min-w-0 shrink'}
      testId="cita-marca-paciente"
    />
  ));
  if (ajuste === 'suelta') return <>{etiquetas}</>;
  return (
    <span className={CLASE_MARCAS_AJUSTE[ajuste]} data-testid={`cita-marcas-${citaId}`} data-ajuste={ajuste}>
      {etiquetas}
    </span>
  );
}

// Cuántas intervenciones se ven en la rejilla (14/10/2026; revisión del mismo
// día), de más a menos sitio:
//   · `todas`:    todas, por fecha;
//   · `plegadas`: la más reciente y «+N» con las demás;
//   · `solo`:     solo «+N» con todas (no cabe ni una entera).
// El título de cada una las dice todas.
const NIVEL_TODAS = 'todas';
const NIVEL_PLEGADAS = 'plegadas';
const NIVEL_SOLO = 'solo';
// Sin medir todavía (o sin pantalla que medir): los distintivos donde
// siempre y el nivel por defecto de cada sitio.
const SIN_MEDIR = { bajar: false, nivel: null };
// La confirmación tal y como va en la línea de la hora (y su copia medida).
const CLASE_CONFIRMADA_LINEA = 'inline-flex items-center rounded px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wide bg-[#16A34A] text-white leading-none whitespace-nowrap';

/**
 * Etiquetas rojas junto al nombre con las intervenciones YA realizadas del
 * paciente: «Cirugía — dd/mm/aaaa» y «Retoque — dd/mm/aaaa», por fecha
 * (14/10/2026; antes solo la última cirugía y el último retoque). Vienen
 * calculadas del backend en `cita.cirugias_previas`. Así en una revisión se ve
 * al instante que es un paciente operado y cuándo.
 * `ajuste` ('linea' | 'fila'): dónde van (ver arriba); sin él, sueltas.
 * `nivel`: cuántas caben (ver arriba). Lo decide `CitaBlock` midiendo.
 */
function CirugiasPrevias({ cita, ajuste = null, nivel = NIVEL_TODAS }) {
  const previas = ordenarIntervenciones(cita.cirugias_previas);
  if (!previas.length) return null;
  const titulo = tituloIntervenciones(previas);
  let visibles = previas;
  if (nivel === NIVEL_SOLO) visibles = [];
  else if (nivel === NIVEL_PLEGADAS) visibles = previas.slice(-1);
  const resto = previas.length - visibles.length;
  return (
    <>
      {visibles.map((qx, i) => (
        <span
          key={`${qx.tipo}-${qx.date}`}
          className={CLASE_QX_PREVIA_AJUSTE[ajuste]
            || `${i === 0 ? 'ml-1.5' : 'ml-1'} ${CLASE_QX_PREVIA_SUELTA}`}
          data-testid="cita-cirugia-previa"
          data-qx-previa="1"
          title={titulo}
        >
          {textoIntervencion(qx)}
        </span>
      ))}
      {resto > 0 && (
        <span
          className={CLASE_QX_MAS_AJUSTE[ajuste] || `ml-1 inline-flex items-center align-middle ${CLASE_QX_MAS}`}
          data-testid="cita-cirugias-mas"
          data-qx-previa="1"
          data-nivel={nivel}
          title={titulo}
          aria-label={titulo}
        >
          +{resto}
        </span>
      )}
    </>
  );
}

/**
 * La copia invisible de las etiquetas en sus tres tamaños (todas, plegadas y
 * solo el «+N») y de los demás distintivos tal y como van en la línea de la
 * hora (`demas`), para medir qué cabe. Fuera del flujo, sin ocupar sitio, sin
 * leerse y sin recibir clics.
 */
function MedidorDePrevias({ previas, medidorRef, citaId, demas = null }) {
  const fila = (contenido, nivel) => (
    <span data-medida={nivel} className="inline-flex w-max items-center gap-1 whitespace-nowrap">
      {contenido}
    </span>
  );
  const etiqueta = (qx) => (
    <span key={`${qx.tipo}-${qx.date}`} className={CLASE_QX_PREVIA_SUELTA}>{textoIntervencion(qx)}</span>
  );
  return (
    <span
      ref={medidorRef}
      aria-hidden="true"
      className="invisible pointer-events-none absolute left-0 top-0 flex flex-col items-start"
      data-testid={`cita-medidor-previas-${citaId}`}
    >
      {fila(previas.map(etiqueta), NIVEL_TODAS)}
      {fila(<>{etiqueta(previas[previas.length - 1])}{previas.length > 1
        ? <span className={CLASE_QX_MAS}>+{previas.length - 1}</span> : null}</>, NIVEL_PLEGADAS)}
      {fila(<span className={CLASE_QX_MAS}>+{previas.length}</span>, NIVEL_SOLO)}
      {demas ? fila(demas, MEDIDA_DEMAS) : null}
    </span>
  );
}

const HUECO_PX = 4;  // el `gap-1` de las filas de la cita
const MINIMO_ETIQUETA_FILA_PX = 48;  // el `basis-12` de la etiqueta en la fila estrecha

/** El ancho de una de las tres copias del medidor. */
function anchoMedido(medidor, nivel) {
  return medidor.querySelector(`[data-medida="${nivel}"]`)?.scrollWidth || 0;
}

/** Lo que miden, juntos, los distintivos que no son de intervención. */
function anchoDeLosDemas(elementos) {
  return elementos
    .filter((el) => !el.hasAttribute('data-qx-previa'))
    .reduce((suma, el) => suma + (el.scrollWidth || 0) + HUECO_PX, 0);
}

const MEDIDA_DEMAS = 'demas';

/** El ancho de dentro de una fila (sin su relleno), o null si no tiene. */
function anchoUtil(fila) {
  const ancho = fila?.clientWidth || 0;
  if (!ancho) return null;
  const estilo = window.getComputedStyle ? window.getComputedStyle(fila) : null;
  return ancho - (parseFloat(estilo?.paddingLeft) || 0) - (parseFloat(estilo?.paddingRight) || 0);
}

/**
 * Qué cabe en la cita (14/10/2026; revisión del mismo día): `{ bajar, nivel }`,
 * o `null` si aún no hay ancho (sin maquetar): entonces no se decide nada.
 *
 *  · En la línea de la hora tienen que caber la hora entera, los demás
 *    distintivos ENTEROS (RESERVA, ONLINE, «✓», medidos en su copia) y
 *    al menos el «+N». Si caben, `nivel` dice cuántas intervenciones van:
 *    todas, la más reciente y «+N», o solo «+N».
 *  · Si no caben (`bajar`), los distintivos bajan a su fila, debajo del
 *    nombre, como en la columna estrecha de varias agendas (5/10/2026):
 *    enteros o a la línea siguiente. Ahí la etiqueta se puede cortar si
 *    tiene al menos sus 3rem; si no, solo «+N». Con RESERVA la fila tiene
 *    dos líneas a la vista y la segunda cuenta entera.
 *  · `estrecha` (la columna estrecha): siempre en la fila.
 * La decisión de bajar se toma con las copias del medidor, no con lo que se
 * está viendo: así no salta de una forma a la otra.
 */
function medirLaCita({ estrecha, dosLineas, filaHora, hora, filaAbajo, medidor, cuantas }) {
  if (!filaHora || !medidor) return null;
  const linea = anchoUtil(filaHora);
  if (linea === null) return null;
  const solo = anchoMedido(medidor, NIVEL_SOLO);
  let bajar = !!estrecha;
  if (!bajar) {
    const demas = anchoMedido(medidor, MEDIDA_DEMAS);
    // Un píxel de margen: el redondeo del navegador no puede cortar la hora.
    const disponible = linea - (hora?.scrollWidth || 0) - (demas ? demas + HUECO_PX : 0)
      - HUECO_PX - 1;
    if (solo <= disponible) {
      if (anchoMedido(medidor, NIVEL_TODAS) <= disponible) return { bajar: false, nivel: NIVEL_TODAS };
      if (cuantas > 1 && anchoMedido(medidor, NIVEL_PLEGADAS) <= disponible) {
        return { bajar: false, nivel: NIVEL_PLEGADAS };
      }
      return { bajar: false, nivel: NIVEL_SOLO };
    }
    bajar = true;
  }
  const fila = anchoUtil(filaAbajo);
  if (fila === null) return { bajar, nivel: null };
  let disponible = fila - anchoDeLosDemas(Array.from(filaAbajo.children));
  if (dosLineas) disponible = Math.max(disponible, fila);
  disponible -= 1;
  const mas = cuantas > 1 ? HUECO_PX + solo : 0;
  const minimo = Math.min(anchoMedido(medidor, NIVEL_PLEGADAS), MINIMO_ETIQUETA_FILA_PX + mas);
  return { bajar, nivel: minimo <= disponible ? NIVEL_PLEGADAS : NIVEL_SOLO };
}

// Cita CONFIRMADA por el paciente desde el enlace del recordatorio
// (2026-09-03, dirección: «debe quedar marcada automáticamente en verde en la
// agenda como Confirmada»). Verde propio, distinto del de «Realizada». Desde
// el 4/10/2026 es la etiqueta «✓ Confirmada» y el borde, NO el fondo
// (dirección: «el color de mi agenda no tiene sentido»): con el fondo verde,
// una consulta previa y una revisión confirmadas salían iguales y el color
// del tipo de cita solo se veía en las que no habían confirmado.
const VERDE_CONFIRMADA = '#16A34A';
// Cuánto se nota el color del tipo de cita en el fondo del bloque (4/10/2026:
// antes 0,12, y en pantalla los tipos casi no se distinguían).
const TINTE_TIPO = 0.2;
// La reserva va en el COLOR del rectángulo entero (15/10/2026): amarillo sin
// pagar, azul y verde pagada; son colores llenos, con su versión oscura
// (`colorDeLaReserva`, variables de index.css), no un tinte del tipo.
// Gris de la cita «no acude» (2026-09-08, dirección, punto 12): se queda en su
// hueco, tachada y apagada, sin el color del concepto.
const GRIS_NO_ACUDE = '#64748B';

// AGENDA BLOQUEADA EL DÍA ENTERO (administración, 2026-09-17): «icono de candado y
// toda la columna de la agenda en color rojo, que quede claro de un vistazo».
// Antes el día completo salía AZUL, que se confundía con una cirugía. El rojo
// intenso (#B91C1C) se usa en las clases de Tailwind —que necesitan el literal
// escrito— y este de aquí en los estilos en línea (tintes calculados).
const ROJO_BLOQUEO = '#DC2626';
// Y las citas que ya estaban dadas ANTES del bloqueo se apartan a este % del
// ancho de la columna, a la derecha: encima del rojo no se leían (administración,
// mismo día). Va en porcentaje a propósito: mida lo que mida la columna, la
// cita ni se sale de ella ni pisa la de al lado.
const LATERAL_CITAS_PCT = 56;
// El rótulo con el candado y el MOTIVO va a ancho completo de la columna, no
// dentro del rectángulo rojo recortado: ahí se quedaba en el 44% de la
// izquierda y en vista semana (~190 px de columna) «Congreso en Valencia» salía
// cortado y el bloqueo no decía por qué (2026-09-17). Como en vista de día las
// citas empiezan en el píxel 0, se les reserva esta banda de arriba para que el
// rótulo no las tape.
const BANDA_ROTULO_PX = 30;

// Flujo del día: color e inicial por estado del paciente.
const ESTADO_STYLES = {
  espera: { color: '#D97706', label: 'Sala de espera' },
  // Enfermería en el flujo de la consulta (administración, 15/10/2026), con
  // su color, como los demás: en la línea de la hora y en el fondo de la cita.
  // El visto, con su nombre corto en la línea (no le quita sitio a RESERVA,
  // ONLINE ni a las intervenciones) y entero en el título de la cita.
  enfermeria: { color: '#DB2777', label: 'Enfermería' },
  visto_enfermeria: { color: '#0891B2', label: 'Visto enf.', titulo: 'Visto por enfermería' },
  tratamiento: { color: '#2563EB', label: 'Consulta' },
  // Quirófano: postoperatorio ya explicado, puede pasar a cambiarse.
  postop_ok: { color: '#7C3AED', label: 'Listo para cambiarse' },
  realizada: { color: '#059669', label: 'Realizada' },
  // Quirófano: ya ha abandonado la clínica (realizada = en recuperación).
  se_fue: { color: '#64748B', label: 'Se fue' },
  // Chalazión en quirófano (30/9/2026): no se ha realizado el tratamiento.
  // No se atenúa como «Realizada»: no está hecho. Ocre y no rojo: en rojo
  // parecía una cita bloqueada (revisión del 30/9/2026).
  no_realizada: { color: '#A16207', label: 'No realizado' },
};

function CitaBlock({
  item, pxPerMin, minStart, color, onCitaClick,
  consultaConjunta = false, esQuirofano: agendaDeQuirofano = false,
  // `bloqueada`: la agenda está bloqueada el día entero y esta cita venía de
  // antes del bloqueo. Se aparta al lateral y se pinta en rojo (2026-09-17).
  bloqueada = false,
  // `compacta`: la vista semana, con columnas estrechas (5/10/2026).
  compacta = false,
  // Varias agendas a la vez (administración, 5/10/2026: «todas las agendas
  // seleccionadas visibles de un vistazo»). `ajustada`: la columna mide lo que
  // le toque, así que nada puede salirse por la derecha de la cita.
  // `estrecha`: columna muy estrecha; los distintivos bajan debajo del
  // nombre, que es lo que manda, y la confirmación va en su forma corta «✓».
  // Con una sola agenda no llega ninguno de los dos: se ve como siempre.
  ajustada = false,
  estrecha = false,
}) {
  const { cita, start, end } = item;
  // Una consulta citada en quirófano (una revisión, 2/10/2026) va en todo como
  // en la agenda de consultas: su círculo de sala de espera y sus estados. Lo
  // dice el servidor en la cita (`consulta_en_quirofano`).
  const esQuirofano = agendaDeQuirofano && !esConsultaEnQuirofano(cita);
  // top/height los calcula layoutCitas (apilado a ancho completo). Fallback por si acaso.
  const top = item.top != null ? item.top : (start - minStart) * pxPerMin;
  const height = item.height != null ? item.height : Math.max(14, (end - start) * pxPerMin - 2);
  const tiny = height < 36;
  // El check de enfermería solo aplica en agendas de consulta conjunta: la
  // de la CITA, que la calcula quien pinta el bloque (24/9/2026).
  const showVisto = !esQuirofano && consultaConjunta && cita.visto_enfermeria;
  // Cita prestada de otra agenda (solo en la agenda de enfermería).
  const origen = cita.agenda_origen || null;
  // «No acude» (dirección, 2026-09-08, punto 12): la cita se queda en su hueco
  // con el nombre tachado y en gris, para que conste que estaba citada y el
  // paciente no se presentó. No lleva círculo de llegada ni verde de
  // confirmada, y va por debajo de una cita activa dada en ese mismo hueco.
  const noAcude = esNoAcude(cita);
  // Consulta online: su distintivo amarillo, como en el parte (5/10/2026).
  const online = esOnline(cita.concept_code);
  // LOPD sin firmar o consentimiento pendiente: el círculo «!» de arriba a la
  // izquierda.
  const avisoDocs = cita.lopd_ok === false || !!cita.consent_pendiente;
  const tituloAvisoDocs = [
    cita.lopd_ok === false ? 'LOPD pendiente' : null,
    cita.consent_pendiente ? 'Consentimiento sin firmar' : null,
  ].filter(Boolean).join(' · ');
  // Datos clínicos obligatorios en blanco en la ficha (dirección, 6/10/2026):
  // alergias, ácido hialurónico previo, medicación habitual. Un segundo «!»,
  // en ROJO, al lado del de documentación. Qué falta lo dice el servidor
  // (`riesgo_clinico.datos_clinicos_que_faltan`), la misma regla que decide
  // si el recordatorio lleva el cuestionario de salud.
  const faltanDatos = Array.isArray(cita.datos_clinicos_pendientes) ? cita.datos_clinicos_pendientes : [];
  const avisoDatos = faltanDatos.length > 0;
  const tituloAvisoDatos = avisoDatos ? `Faltan datos clínicos: ${faltanDatos.join(', ')}` : '';
  const avisos = (avisoDocs ? 1 : 0) + (avisoDatos ? 1 : 0);
  // RESERVA (12/10 y 15/10/2026; el estado lo decide el servidor,
  // `estado_reserva`). Se ve en el COLOR del rectángulo entero y en el nombre:
  //  · sin pagar → AMARILLO, la etiqueta RESERVA y «Reserva <tipo>»;
  //  · cirugía con la reserva pagada y el resto por cobrar → AZUL;
  //  · pagada del todo (cirugía cubierta, o el pago que la deja verde) → VERDE;
  //  · pagada en su color, o quitada a mano → el color de su tipo.
  // El título dice por qué (cuánto falta, solo a gestión).
  const reservaSinPagar = cita.estado_reserva === ESTADO_RESERVA.PENDIENTE;
  const restoPendiente = cita.estado_reserva === ESTADO_RESERVA.PAGADA_PARCIAL;
  const colorReserva = colorDeLaReserva(cita.estado_reserva);
  // Sin pagar y con el resto por cobrar, el color de la reserva MANDA sobre
  // el del estado del día (sala de espera, realizada…): es lo que no se
  // puede perder de vista (revisión del 15/10/2026). El verde, no.
  const reservaManda = reservaSinPagar || restoPendiente;
  const colorBorde = colorReserva ? colorReserva.borde : color;
  const tipoCita = nombreTipoDeLaCita(cita);
  const tituloReserva = reservaSinPagar ? 'RESERVA sin pagar'
    : cita.estado_reserva === ESTADO_RESERVA.PAGADA_PARCIAL
      ? (cita.estado_reserva_titulo || 'Pendiente de pago')
      : cita.estado_reserva === ESTADO_RESERVA.PAGADA_VERDE ? (cita.estado_reserva_titulo || 'Pagada') : '';

  // Estado del paciente en el día (Sala de espera / Enfermería / Visto por
  // enfermería / Consulta / Realizada).
  // "Sala de espera" es el ÚNICO marcador de llegada: un clic en el círculo
  // (solo citas de HOY) lo activa/desactiva. Ya no hay código de luz aparte.
  const esHoy = cita.date === todayStr();
  const [estadoPac, setEstadoPac] = useState(cita.estado_paciente || null);
  const [savingEstado, setSavingEstado] = useState(false);
  useEffect(() => { setEstadoPac(cita.estado_paciente || null); }, [cita.estado_paciente]);
  // El circuito del día (sala de espera → …) vale para toda la agenda, no
  // solo quirófano (2026-07-28: una restricción de un par de días antes
  // había apagado sin querer la luz de llegada en las consultas normales,
  // que es donde más se usa).
  const estiloEstado = ESTADO_STYLES[estadoPac] || null;
  const estado = (esQuirofano && ['espera', 'enfermeria', 'visto_enfermeria', 'tratamiento'].includes(estadoPac))
    ? null
    // El chalazión de quirófano dice «Realizado», como su botón (30/9/2026).
    : (estadoPac === 'realizada' && esChalazionDeQuirofano(cita)
      ? { ...estiloEstado, label: 'Realizado' }
      : estiloEstado);
  const enEspera = !esQuirofano && estadoPac === 'espera';
  // ¿Se pinta el círculo de sala de espera? (citas de HOY, o ya en espera).
  const conCirculo = !esQuirofano && !noAcude && (enEspera || esHoy);
  // Confirmada por el paciente: etiqueta y borde verdes mientras no haya un
  // estado del día (sala de espera, consulta, realizada…), que sigue
  // mandando — la confirmación importa ANTES de que el paciente llegue. El
  // fondo sigue siendo el de su tipo de cita (4/10/2026).
  const confirmada = cita.paciente_confirmacion === 'confirmada' && !estado && !noAcude;
  const tinte = noAcude
    ? hexToRgba(GRIS_NO_ACUDE, 0.10)
    : bloqueada
      ? hexToRgba(ROJO_BLOQUEO, 0.14)
      : reservaManda
        ? colorReserva.fondo
        : estado
          ? hexToRgba(estado.color, 0.14)
          : colorReserva ? colorReserva.fondo : hexToRgba(color, TINTE_TIPO);

  // INTERVENCIONES REALIZADAS (14/10/2026): se acumulan, y no siempre caben.
  // Se mide de verdad —con una copia invisible, que no ocupa sitio— al pintar
  // y cada vez que la columna cambia de ancho (`medirLaCita`): cuántas van
  // (todas, la más reciente y «+N», o solo «+N») y si los demás distintivos
  // (RESERVA, ONLINE, «✓») caben ENTEROS en la línea de la hora con la
  // hora entera; si no, bajan con ellas a su fila, debajo del nombre. Sin
  // medir (pantalla sin maquetar): en la línea de la hora, todas; en la fila
  // estrecha, plegadas, como hasta ahora.
  const previas = useMemo(() => ordenarIntervenciones(cita.cirugias_previas), [cita.cirugias_previas]);
  const medirPrevias = previas.length > 0 && !tiny;
  const filaHoraRef = useRef(null);
  const horaRef = useRef(null);
  const distintivosRef = useRef(null);
  const filaEstrechaRef = useRef(null);
  const medidorPreviasRef = useRef(null);
  const [medida, setMedida] = useState(SIN_MEDIR);
  // Los distintivos van en su fila, debajo del nombre: en la columna estrecha
  // siempre; en las demás, si en la línea de la hora no caben enteros.
  const enFila = estrecha || medida.bajar;
  const nivelPrevias = medida.nivel || (enFila ? NIVEL_PLEGADAS : NIVEL_TODAS);
  const hayDemas = reservaSinPagar || restoPendiente || online || confirmada;
  // Lo que cambia el ancho que piden: las etiquetas y lo que va a su lado.
  const firmaLinea = [
    previas.map(textoIntervencion).join('|'), estado?.label || '', noAcude ? 'na' : '',
    reservaSinPagar ? 'r' : '', restoPendiente ? 'p' : '', online ? 'o' : '',
    confirmada ? 'c' : '', compacta ? 'k' : '', ajustada ? 'a' : '', estrecha ? 'x' : '',
    cita.start || '', cita.end || '',
  ].join('·');
  useLayoutEffect(() => {
    if (!medirPrevias) {
      setMedida(SIN_MEDIR);
      return undefined;
    }
    const medir = () => {
      const r = medirLaCita({
        estrecha,
        dosLineas: reservaSinPagar,
        filaHora: filaHoraRef.current,
        hora: horaRef.current,
        filaAbajo: filaEstrechaRef.current,
        medidor: medidorPreviasRef.current,
        cuantas: previas.length,
      });
      if (r) setMedida((antes) => (antes.bajar === r.bajar && antes.nivel === r.nivel ? antes : r));
    };
    medir();
    const fila = filaHoraRef.current;
    if (!fila || typeof window.ResizeObserver === 'undefined') return undefined;
    const ro = new window.ResizeObserver(() => medir());
    ro.observe(fila);
    return () => ro.disconnect();
    // `firmaLinea` resume lo que cambia el ancho (las etiquetas, los otros
    // distintivos, la hora y el estado); con los distintivos ya bajados
    // (`medida.bajar`) se mide la fila de abajo.
  }, [medirPrevias, firmaLinea, estrecha, reservaSinPagar, previas.length, medida.bajar]);

  // MARCAS DE PACIENTE (administración, 21/10/2026): su etiqueta, en el
  // color de la marca, junto al nombre (`MarcasEnLaCita`).
  const marcasCita = useMemo(() => marcasDeLaCita(cita.marcas_paciente), [cita.marcas_paciente]);
  const marcasAlLado = marcasCita.length > 0 && !estrecha;
  const nombreCita = (
    <div
      className={`${estrecha
        ? 'text-[14px] leading-[1.1] line-clamp-2 break-words'
        : 'text-[15px] leading-tight truncate'}${marcasAlLado ? ' min-w-0' : ''} font-semibold ${
        noAcude ? 'line-through text-[#13231E]/60' : 'text-[#13231E]'
      }`}
      data-testid={`cita-nombre-${cita.id}`}
    >
      {/* Paciente PROHIBIDO (14/10/2026): pequeño y rojo, delante del
          nombre para que no lo corte el «…» ni tape otros distintivos. */}
      <IndicadorProhibido prohibido={cita.prohibido} motivo={cita.prohibido_motivo} tamano="mini"
        interactivo={false} className="mr-1" testId={`cita-prohibido-${cita.id}`} />
      {cita.patient_name}
    </div>
  );

  const toggleEspera = async (e) => {
    e.stopPropagation();
    if (savingEstado || !esHoy) return;
    const next = enEspera ? null : 'espera';
    const prev = estadoPac;
    setEstadoPac(next); // optimista
    setSavingEstado(true);
    try {
      await setEstadoCita(cita.id, next);
    } catch (err) {
      setEstadoPac(prev);
      toast.error(err?.response?.data?.detail || 'No se pudo marcar sala de espera');
    } finally {
      setSavingEstado(false);
    }
  };

  return (
    <button
      type="button"
      data-testid={`agenda-cita-${cita.id}`}
      data-confirmada={confirmada ? '1' : undefined}
      data-no-acude={noAcude ? '1' : undefined}
      data-bloqueada={bloqueada ? '1' : undefined}
      data-reserva={cita.estado_reserva || undefined}
      // El tinte del fondo y el color del borde, a la vista de las pruebas
      // (jsdom no lee el degradado de `background` ni un `var()` en el borde).
      data-tinte={tinte}
      data-borde={colorReserva ? colorReserva.borde : undefined}
      onClick={(e) => {
        e.stopPropagation();
        onCitaClick?.(cita, e);
      }}
      className={`absolute rounded-md border bg-white text-left overflow-hidden cursor-pointer shadow-sm hover:shadow-md hover:z-[12] transition-[box-shadow,transform] active:scale-[0.99] ${
        noAcude ? 'z-[8] opacity-70 border-dashed' : 'z-[10]'
      } ${cita.estado_paciente === 'realizada' ? 'opacity-60' : ''}`}
      style={{
        top,
        height,
        // Agenda bloqueada el día entero: la cita se aparta al lateral DERECHO
        // de su propia columna, para que el rojo del bloqueo no se la coma
        // (administración, 2026-09-17). El resto de días, a ancho completo de siempre.
        left: bloqueada ? `${100 - LATERAL_CITAS_PCT}%` : 2,
        right: 2,
        borderLeft: `4px solid ${noAcude ? GRIS_NO_ACUDE : bloqueada ? ROJO_BLOQUEO : colorBorde}`,
        borderColor: noAcude
          ? hexToRgba(GRIS_NO_ACUDE, 0.6)
          : bloqueada
            ? hexToRgba(ROJO_BLOQUEO, 0.75)
            : estado
              ? hexToRgba(estado.color, 0.55)
              : confirmada ? hexToRgba(VERDE_CONFIRMADA, 0.6) : 'hsl(var(--border))',
        borderLeftColor: noAcude ? GRIS_NO_ACUDE : bloqueada ? ROJO_BLOQUEO : colorBorde,
        // Base de tarjeta (clara/oscura según el tema) + tinte de color encima:
        // la cita se lee bien tanto en claro como en oscuro (2026-07-17).
        background: `linear-gradient(${tinte}, ${tinte}), hsl(var(--card))`,
      }}
      title={[
        `${cita.start}–${cita.end || ''} · ${cita.patient_name || ''} · ${tipoCita}`,
        cita.prohibido ? tituloProhibido(cita.prohibido_motivo) : '',
        ...marcasCita.map((m) => tituloMarca(m.texto)),
        cita.procedimiento || '',
        cita.notes || '',
        tituloReserva,
        noAcude
          ? `✗ No acudió${cita.no_acude_by_name ? ` · marcado por ${cita.no_acude_by_name}` : ''}`
          : cita.paciente_confirmacion === 'confirmada' ? '✓ Confirmada por el paciente' : '',
        bloqueada ? 'Agenda bloqueada ese día — cita dada antes del bloqueo' : '',
        showVisto ? '✓ Visto por enfermería' : '',
        avisoDocs ? `! ${tituloAvisoDocs}` : '',
        avisoDatos ? `! ${tituloAvisoDatos}` : '',
        estado ? `● ${estado.titulo || estado.label}` : '',
        tituloIntervenciones(previas),
      ].filter(Boolean).join('\n')}
    >
      {/* Sala de espera: un clic marca/desmarca (citas de HOY, cualquier agenda).
          En una «no acude» no tiene sentido: no hay llegada que marcar. */}
      {conCirculo && (
        <span
          role="button"
          tabIndex={-1}
          onClick={toggleEspera}
          title={enEspera
            ? `En sala de espera${esHoy ? ' — pulsa para quitar' : ''}`
            : 'Pulsa cuando el paciente llegue (sala de espera)'}
          data-testid={`cita-espera-${cita.id}`}
          className={`absolute top-0.5 right-0.5 z-[13] inline-block w-3.5 h-3.5 rounded-full transition-colors ${
            enEspera
              ? 'bg-amber-500 shadow-[0_0_4px_rgba(217,119,6,0.7)]'
              : 'bg-white border-2 border-slate-300 hover:border-amber-400'
          } ${esHoy ? 'cursor-pointer' : 'cursor-default'} ${savingEstado ? 'opacity-50' : ''}`}
        />
      )}

      {/* Consulta conjunta: ya visto por enfermería → check verde */}
      {showVisto && (
        <span
          className="absolute top-0.5 right-5 z-[11] inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-emerald-500 text-white text-[9px] leading-none"
          data-testid="cita-visto-enfermeria-badge"
          aria-label="Visto por enfermería"
        >
          ✓
        </span>
      )}
      {/* Aviso de documentación pendiente (LOPD sin firmar / consentimiento
          sin firmar). lopd_ok===false → el paciente no tiene LOPD.
          Un «!» y no el emoji de aviso (administración, 5/10/2026: «los
          emojis no se renderizan correctamente en el sistema»). */}
      {avisoDocs && !tiny && (
        <span
          className="absolute top-0.5 left-1 z-[13] inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-white text-[11px] font-bold leading-none shadow"
          data-testid="cita-aviso-docs"
          title={tituloAvisoDocs}
          role="img"
          aria-label={tituloAvisoDocs}
        >
          !
        </span>
      )}
      {avisoDatos && !tiny && (
        <span
          className={`absolute top-0.5 ${avisoDocs ? 'left-[1.375rem]' : 'left-1'} z-[13] inline-flex items-center justify-center w-4 h-4 rounded-full bg-red-600 text-white text-[11px] font-bold leading-none shadow`}
          data-testid="cita-aviso-datos"
          title={tituloAvisoDatos}
          role="img"
          aria-label={tituloAvisoDatos}
        >
          !
        </span>
      )}
      {tiny ? (
        <div className="px-1.5 leading-[16px] text-[13.5px] truncate">
          {avisoDocs && (
            <span
              className="mr-1 align-middle font-bold text-amber-600"
              title={tituloAvisoDocs}
              role="img"
              aria-label={tituloAvisoDocs}
            >!</span>
          )}
          {avisoDatos && (
            <span
              className="mr-1 align-middle font-bold text-red-600"
              data-testid="cita-aviso-datos"
              title={tituloAvisoDatos}
              role="img"
              aria-label={tituloAvisoDatos}
            >!</span>
          )}
          {estado && (
            <span
              className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle"
              style={{ backgroundColor: estado.color }}
              aria-label={estado.titulo || estado.label}
              data-testid={`cita-estado-punto-${cita.id}`}
              data-estado={estadoPac || undefined}
            />
          )}
          {/* En la cita corta, la confirmación va en su etiqueta verde
              rellena (revisión del 4/10/2026): sin el fondo verde, un «✓»
              suelto casi no se veía sobre el color del tipo. */}
          {confirmada && (
            <span
              className="mr-1 inline-flex items-center rounded px-1 align-middle text-[10px] font-bold leading-none bg-[#16A34A] text-white"
              data-testid="cita-confirmada-badge"
              title="Confirmada por el paciente"
            >✓</span>
          )}
          {bloqueada && (
            <Lock className="inline-block w-3 h-3 mr-0.5 align-[-1px] text-[#B91C1C]" aria-hidden="true" />
          )}
          <span className={`tabular-nums ${bloqueada ? 'font-semibold text-[#B91C1C]' : 'text-[#13231E]'}`}>{cita.start}</span>{' '}
          <IndicadorProhibido prohibido={cita.prohibido} motivo={cita.prohibido_motivo} tamano="mini"
            interactivo={false} className="mr-1" testId={`cita-prohibido-${cita.id}`} />
          <span
            className={`font-semibold ${noAcude ? 'line-through text-[#13231E]/60' : 'text-[#13231E]'}`}
            data-testid={`cita-nombre-${cita.id}`}
          >{cita.patient_name}</span>
          {noAcude && (
            <span className="ml-1 font-semibold text-amber-700" data-testid={`cita-no-acude-${cita.id}`}>· No acude</span>
          )}
          {/* Las marcas de paciente, detrás del nombre (21/10/2026). */}
          <MarcasEnLaCita marcas={marcasCita} citaId={cita.id} ajuste="suelta" />
          {/* En la cita corta, ONLINE va DESPUÉS del nombre (revisión del
              5/10/2026): delante, en la vista semana, dejaba el nombre en
              casi nada. */}
          {/* La reserva, también después del nombre (12/10/2026). */}
          {reservaSinPagar && <span className="ml-1"><EtiquetaReserva /></span>}
          {restoPendiente && <span className="ml-1"><EtiquetaRestoPendiente /></span>}
          {online && <span className="ml-1"><EtiquetaOnline /></span>}
          <CirugiasPrevias cita={cita} nivel={NIVEL_PLEGADAS} />
          {cita.notes ? <span className="text-muted-foreground italic"> · {cita.notes}</span> : null}
        </div>
      ) : (
        <div className="px-1.5 py-0.5 min-w-0">
          {/* Con el círculo de sala de espera arriba a la derecha, la línea le
              deja su hueco: tapaba el final de la última etiqueta. Con varias
              agendas a la vez (5/10/2026) también se le deja al check de
              enfermería y, a la izquierda, al «!» de documentación: en una
              columna estrecha la hora solo está aquí y no puede quedar tapada.
              Desde el 6/10/2026, en todas las vistas y también para el «!»
              rojo de datos clínicos: con dos avisos tapaban la hora. */}
          <div
            ref={filaHoraRef}
            className={`flex items-center gap-1 leading-tight ${
              ajustada && showVisto ? 'pr-9' : conCirculo ? 'pr-4' : ''
            } ${avisos === 2 ? 'pl-9' : avisos === 1 ? 'pl-4' : ''}`}
            data-testid={`cita-linea-hora-${cita.id}`}
          >
            <span ref={horaRef} className={`text-[10px] tabular-nums truncate ${
              bloqueada ? 'font-semibold text-[#B91C1C]' : 'text-muted-foreground'
            }`}>
              {bloqueada && (
                <Lock className="inline-block w-3 h-3 mr-0.5 align-[-1px]" aria-hidden="true" />
              )}
              {cita.start}–{cita.end || toHHMM(end)}
              {estado && !enEspera && estadoPac !== 'postop_ok' && (
                <span
                  className="font-medium not-italic"
                  style={{ color: estado.color }}
                  data-testid={`cita-estado-texto-${cita.id}`}
                  data-estado={estadoPac || undefined}
                  title={estado.titulo || undefined}
                >
                  {' '}· {estado.label}
                </span>
              )}
              {noAcude && (
                <span className="font-semibold not-italic text-amber-700" data-testid={`cita-no-acude-${cita.id}`}>
                  {' '}· No acude
                </span>
              )}
            </span>
            {/* Los distintivos viven AQUÍ, en el hueco libre de la línea de la
                hora: así no roban sitio al nombre ni a las notas. En una
                columna estrecha (5/10/2026) bajan debajo del nombre.
                Desde el 14/10/2026 en dos grupos, a la derecha: RESERVA,
                ONLINE y la confirmación, ENTEROS (no encogen nunca); detrás,
                las intervenciones, en su propio grupo que encoge el primero
                y enseña las que quepan (`nivelPrevias`). */}
            {!enFila && hayDemas && (
              <span
                ref={distintivosRef}
                className="ml-auto shrink-0 inline-flex items-center gap-1"
                data-testid={ajustada ? `cita-distintivos-${cita.id}` : undefined}
              >
                {/* La reserva sin pagar va la primera: es lo que hay que ver
                    antes de nada. */}
                {reservaSinPagar && <EtiquetaReserva />}
                {restoPendiente && <EtiquetaRestoPendiente />}
                {online && <EtiquetaOnline />}
                {/* En la vista semana, con ONLINE al lado, la confirmación va
                    en su forma corta «✓» (revisión del 5/10/2026): las dos
                    enteras se comían la hora en una columna estrecha. */}
                {confirmada && (
                  <span
                    className={CLASE_CONFIRMADA_LINEA}
                    data-testid="cita-confirmada-badge"
                    title="Confirmada por el paciente desde el enlace del recordatorio"
                  >
                    {compacta && online ? '✓' : '✓ Confirmada'}
                  </span>
                )}
              </span>
            )}
            {!enFila && previas.length > 0 && (
              <span
                className={`${hayDemas ? '' : 'ml-auto '}${
                  nivelPrevias === NIVEL_SOLO ? 'shrink-0' : 'min-w-0 shrink-[50]'
                } overflow-hidden inline-flex items-center gap-1`}
                data-testid={`cita-intervenciones-${cita.id}`}
                data-nivel={nivelPrevias}
              >
                <CirugiasPrevias cita={cita} ajuste="linea" nivel={nivelPrevias} />
              </span>
            )}
          </div>
          {enEspera && (
            <div className={ajustada ? 'mb-0.5 min-w-0' : 'mb-0.5'}>
              <span className={`${ajustada ? 'inline-block max-w-full truncate align-top' : 'inline-flex items-center gap-1'} rounded px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wide bg-amber-500 text-white leading-none`}>
                ● Sala de espera
              </span>
            </div>
          )}
          {/* Aviso de quirófano: postoperatorio explicado, puede pasar a
              cambiarse. Tan visible como el de sala de espera; llega solo con
              el auto-refresco de la agenda. */}
          {estadoPac === 'postop_ok' && (
            <div className={ajustada ? 'mb-0.5 min-w-0' : 'mb-0.5'}>
              <span className={`${ajustada ? 'inline-block max-w-full truncate align-top' : 'inline-flex items-center gap-1'} rounded px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-wide bg-[#7C3AED] text-white leading-none`} data-testid="cita-listo-cambiarse">
                ● Listo para cambiarse
              </span>
            </div>
          )}
          {/* En una columna estrecha (5/10/2026) el nombre, que es lo que
              manda, puede ocupar dos líneas antes de cortarse con «…»: en una
              línea de 60-90 px solo cabía el nombre de pila a medias. Un punto
              más pequeño para que en la cita de 60 px quepan las dos líneas y
              la fila de distintivos. Sin guiones de sílaba: partía nombres
              que cabían enteros en la línea de abajo. */}
          {/* Las marcas de paciente (21/10/2026), a la derecha del nombre;
              en la columna estrecha, en su fila justo debajo. */}
          {marcasAlLado ? (
            <div className="flex items-center gap-1.5 min-w-0" data-testid={`cita-nombre-y-marcas-${cita.id}`}>
              {nombreCita}
              <MarcasEnLaCita marcas={marcasCita} citaId={cita.id} ajuste="nombre" />
            </div>
          ) : nombreCita}
          {estrecha && <MarcasEnLaCita marcas={marcasCita} citaId={cita.id} ajuste="fila" />}
          {/* Columna estrecha (varias agendas a la vez, 5/10/2026): los
              distintivos van DEBAJO del nombre, en UNA fila visible. Así nunca
              le quitan sitio ni lo tapan. Van por orden de importancia: ONLINE
              y la confirmación (en su forma corta «✓»), enteros; la cirugía
              previa, cortada con «…» si no cabe entera (el título la dice
              toda). Lo que no cabe ni así pasa a una segunda fila que no se
              ve: entero o nada, sin trozos cortados por la mitad (la cita
              corta mide 60 px). La confirmación sigue a la vista en el borde
              verde de la cita.
              Con la RESERVA sin pagar (12/10/2026), que va la primera, la
              fila puede pasar a DOS líneas y la etiqueta dice «RES.»: si no,
              en una columna de 60-90 px se comía ONLINE y la «✓». */}
          {enFila && (hayDemas || previas.length > 0) && (
            <div
              ref={filaEstrechaRef}
              className={`flex flex-wrap items-start gap-1 min-w-0 ${
                reservaSinPagar ? 'max-h-8' : 'max-h-3.5'} overflow-hidden mb-0.5`}
              data-testid={`cita-distintivos-${cita.id}`}
              data-dos-lineas={reservaSinPagar ? '1' : undefined}
            >
              {reservaSinPagar && <span className="shrink-0 inline-flex"><EtiquetaReserva corta /></span>}
              {restoPendiente && <span className="shrink-0 inline-flex"><EtiquetaRestoPendiente corta /></span>}
              {online && <span className="shrink-0 inline-flex"><EtiquetaOnline /></span>}
              {confirmada && (
                <span
                  className="shrink-0 inline-flex items-center rounded px-1.5 py-[1px] text-[10px] font-bold leading-none bg-[#16A34A] text-white whitespace-nowrap"
                  data-testid="cita-confirmada-badge"
                  title="Confirmada por el paciente desde el enlace del recordatorio"
                >✓</span>
              )}
              <CirugiasPrevias cita={cita} ajuste="fila" nivel={nivelPrevias} />
            </div>
          )}
          {cita.notes && (
            <div
              className="text-[12px] text-[#13231E]/80 italic leading-tight overflow-hidden"
              style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
            >{cita.notes}</div>
          )}
          <div className="text-[10px] text-muted-foreground leading-tight truncate">
            {/* La cita prestada dice de qué agenda viene con texto, «Agenda …»
                (5/10/2026: antes era una flecha diagonal, que es un emoji y no
                todos los equipos la pintan bien; «de …» se leía como parte
                del tipo de cita). */}
            {/* El tipo con «Reserva » delante mientras no esté pagada. */}
            {[cita.procedimiento, cita.cirujano, tipoCita, cita.lugar,
              origen ? `Agenda ${origen}` : null].filter(Boolean).join(' · ')}
          </div>
        </div>
      )}
      {medirPrevias && (
        <MedidorDePrevias
          previas={previas}
          medidorRef={medidorPreviasRef}
          citaId={cita.id}
          demas={!estrecha && hayDemas ? (
            <>
              {reservaSinPagar && <EtiquetaReserva copia />}
              {restoPendiente && <EtiquetaRestoPendiente copia />}
              {online && <EtiquetaOnline copia />}
              {confirmada && (
                <span className={CLASE_CONFIRMADA_LINEA}>{compacta && online ? '✓' : '✓ Confirmada'}</span>
              )}
            </>
          ) : null}
        />
      )}
    </button>
  );
}

function BloqueoBlock({ bloqueo, minStart, maxEnd, pxPerMin, citasAlLateral = false }) {
  const from = bloqueo.all_day ? minStart : Math.max(minStart, toMin(bloqueo.start));
  const to = bloqueo.all_day ? maxEnd : Math.min(maxEnd, toMin(bloqueo.end));
  if (to <= from) return null;
  // Día COMPLETO bloqueado: candado y la COLUMNA ENTERA en rojo (administración,
  // 2026-09-17: «que aparezca un icono de candado y toda la columna en color
  // rojo»). Antes salía azul —y solo cubría el rango horario, así que si el
  // día se estiraba al apilar citas el azul se quedaba a medias—: ahora va de
  // arriba abajo de la columna (top/bottom 0), mida lo que mida.
  // Si ese día quedaban citas de ANTES del bloqueo, el rojo deja libre el
  // lateral derecho y ahí se apartan ellas: encima del rojo no se leían.
  // Los bloqueos de TRAMO horario conservan el rayado rojo de siempre.
  if (bloqueo.all_day) {
    return (
      <>
        <div
          className="absolute z-[5] pointer-events-none rounded-sm border border-[#B91C1C]/50 overflow-hidden"
          data-testid={`agenda-bloqueo-${bloqueo.id}`}
          data-dia-completo="1"
          style={{
            top: 0,
            bottom: 0,
            left: 0,
            right: citasAlLateral ? `${LATERAL_CITAS_PCT}%` : 0,
            // Rojo TRANSLÚCIDO, no sólido: se posa sobre el fondo de la columna
            // (que ya va teñido) y sirve igual en claro y en oscuro.
            backgroundColor: hexToRgba(ROJO_BLOQUEO, 0.22),
          }}
        />
        {/* El motivo, a ANCHO COMPLETO de la columna y fuera del rectángulo
            recortado (2026-09-17): dentro vivía en el 44% de la izquierda y en
            vista semana se cortaba justo la parte que importa, el porqué.
            Rojo oscuro con texto blanco: se lee bien encima del tinte rojo. */}
        <div
          className="absolute z-[7] px-1 pt-1 pointer-events-none"
          data-testid={`agenda-bloqueo-rotulo-${bloqueo.id}`}
          style={{ top: 0, left: 0, right: 0 }}
        >
          <div className="flex items-center justify-center gap-1.5 rounded-md bg-[#B91C1C] text-white text-[11px] font-semibold px-2 py-1 shadow-sm">
            <Lock className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">{bloqueo.reason || 'Agenda bloqueada'}</span>
          </div>
        </div>
      </>
    );
  }
  return (
    <div
      className="absolute inset-x-0 z-[5] pointer-events-none rounded-sm border border-red-200 overflow-hidden"
      data-testid={`agenda-bloqueo-${bloqueo.id}`}
      style={{
        top: (from - minStart) * pxPerMin,
        height: (to - from) * pxPerMin,
        background:
          'repeating-linear-gradient(45deg, rgba(220,38,38,0.08) 0px, rgba(220,38,38,0.08) 6px, rgba(220,38,38,0.03) 6px, rgba(220,38,38,0.03) 12px)',
      }}
    >
      <div className="px-1.5 py-0.5 text-[10px] text-red-700/90 truncate">
        <Lock className="inline-block w-3 h-3 mr-1 align-[-2px]" aria-hidden="true" />
        {bloqueo.reason || 'Bloqueado'}
      </div>
    </div>
  );
}

export default function AgendaGrid({
  agenda,
  date,
  dates,
  citas = [],
  bloqueos = [],
  conceptos = [],
  onSlotClick,
  onCitaClick,
  compact = false,
  isAdmin = false,
  showHeaders = false,
  onDateHeaderClick,
  // Las agendas que tiene cargadas la pantalla: una cita prestada (en la de
  // Enfermería) decide su «visto por enfermería» con SU agenda (24/9/2026).
  agendas = [],
  // Varias agendas a la vez (administración, 5/10/2026): «tienen que poder
  // verse todas a la vez en pantalla sin necesidad de hacer scroll
  // horizontal». La rejilla se ajusta al ancho que le toque, sin mínimo.
  ajustarAlAncho = false,
  // Columna estrecha: fuera la regla de horas (56 px que con muchas agendas
  // eran media columna); la hora sigue en la primera línea de cada cita.
  sinRegla = false,
}) {
  const cols = dates && dates.length > 0 ? dates : date ? [date] : [];
  const pxPerMin = compact ? 1.5 : 2.2;
  // Una sola columna (día / agenda suelta) → citas del MISMO tamaño apiladas y
  // la regla de horas alineada a cada cita. Varias columnas (semana) →
  // proporcional, alineado a la regla de horas fija.
  const stack = cols.length === 1;
  const today = todayStr();

  // Línea "ahora": refresco cada minuto solo si algún día visible es hoy.
  const hasToday = cols.includes(today);
  const [nowMin, setNowMin] = useState(() => nowMinutes());
  useEffect(() => {
    if (!hasToday) return undefined;
    const id = setInterval(() => setNowMin(nowMinutes()), 60000);
    return () => clearInterval(id);
  }, [hasToday]);

  const conceptByCode = useMemo(() => {
    const map = {};
    for (const c of conceptos) map[c.code] = c;
    return map;
  }, [conceptos]);

  const bloqueosFor = (dstr) => bloqueos.filter((b) => b.date_from <= dstr && dstr <= b.date_to);
  // Cierres bloquean huecos; aperturas añaden tramos extraordinarios de citas.
  const cierresFor = (dstr) => bloqueosFor(dstr).filter((b) => (b.tipo || 'cierre') !== 'apertura');
  const aperturasFor = (dstr) =>
    bloqueosFor(dstr).filter((b) => (b.tipo || 'cierre') === 'apertura' && b.start && b.end);
  const rangesFor = (dstr) => [
    ...(agenda?.working_hours?.[weekdayKey(dstr)] || []),
    ...aperturasFor(dstr).map((a) => ({ start: a.start, end: a.end })),
  ];

  // Rango visual: primera hora laborable −30 min … última +30 min (fallback 09–21).
  const { minStart, maxEnd } = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const d of cols) {
      for (const r of rangesFor(d)) {
        lo = Math.min(lo, toMin(r.start));
        hi = Math.max(hi, toMin(r.end));
      }
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) {
      lo = 9 * 60;
      hi = 21 * 60;
    }
    return { minStart: Math.max(0, lo - 30), maxEnd: Math.min(24 * 60, hi + 30) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agenda, bloqueos, cols.join('|')]);

  const totalPx = (maxEnd - minStart) * pxPerMin;

  // Marcas cada 30 min (la primera alineada a múltiplo de 30).
  const ticks = useMemo(() => {
    const out = [];
    for (let m = Math.ceil(minStart / 30) * 30; m <= maxEnd; m += 30) out.push(m);
    return out;
  }, [minStart, maxEnd]);

  // Layout de cada día (apilado a ancho completo). Se calcula una vez para
  // saber cuánto "se expande" el día y ajustar la altura total de la rejilla.
  const laidOutByCol = useMemo(() => {
    const map = {};
    for (const d of cols) {
      // Las anuladas no se pintan; las «no acude» sí, en su hueco y tachadas
      // (dirección, 2026-09-08, punto 12): constancia de que estaban citadas.
      const dayCitas = citas.filter((c) => c.date === d && !esCitaAnulada(c));
      const items = layoutCitas(dayCitas, pxPerMin, minStart, stack);
      // Día bloqueado entero en vista de DÍA: las citas empiezan en el píxel 0,
      // así que bajan una banda para dejar sitio al rótulo del motivo, que va a
      // ancho completo arriba del todo (2026-09-17). Bajándolas AQUÍ, la regla
      // de horas, las líneas y el alto de la rejilla las siguen solas. En vista
      // semana no se toca nada: cada cita tiene que quedarse en su hora.
      if (stack && items.length > 0 && cierresFor(d).some((b) => b.all_day)) {
        for (const it of items) it.top += BANDA_ROTULO_PX;
      }
      map[d] = items;
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols.join('|'), citas, bloqueos, pxPerMin, minStart, stack]);

  // La rejilla mide lo normal (rango horario) o más si algún día se expandió
  // al apilar citas solapadas — así el gutter y las columnas van a la par.
  const gridHeight = useMemo(() => {
    let contentBottom = 0;
    for (const d of cols) {
      for (const it of laidOutByCol[d] || []) contentBottom = Math.max(contentBottom, it.top + it.height + 8);
    }
    // Apilado (lista): solo lo que ocupan las citas. Proporcional: el rango
    // horario completo (o más si algo se expandió).
    if (stack) return Math.max(contentBottom, 120);
    return Math.max(totalPx, contentBottom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laidOutByCol, totalPx, cols.join('|'), stack]);

  const handleColumnClick = (e, dstr) => {
    if (!onSlotClick) return;
    // AGENDA BLOQUEADA EL DÍA ENTERO: no se cita encima, y no se cita ni siendo
    // admin (administración, 2026-09-17: «no debe permitir citar nada sobre ella»).
    // Antes el clic pasaba para todo el que puede forzar huecos —dirección,
    // mostrador, enfermería, doctor— y el único freno llegaba al guardar, con
    // un 409 y un botón «Forzar solapamiento» que suena a doble cita, no a «ese
    // día no hay nadie en la clínica»: el paciente se plantaba aquí un día de
    // congreso o de vacaciones. Si de verdad hay que citar ese día, primero se
    // quita el bloqueo, que es un gesto consciente y queda registrado.
    // Los bloqueos de TRAMO horario no entran aquí: esos sí tiene sentido
    // forzarlos, y siguen con la regla de siempre (solo quien puede forzar).
    const bloqueoDiaCompleto = cierresFor(dstr).find((b) => b.all_day);
    if (bloqueoDiaCompleto) {
      toast.error(`Agenda bloqueada: ${bloqueoDiaCompleto.reason || 'sin motivo'}`, {
        description: 'Ese día no se pueden dar citas. Si hace falta, quita antes el bloqueo.',
      });
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const rawMin = minStart + (e.clientY - rect.top) / pxPerMin;
    const slot = agenda?.slot_minutes || 15;
    const snapped = Math.floor(rawMin / slot) * slot;
    if (snapped < 0 || snapped >= 24 * 60) return;
    const inWorking = rangesFor(dstr).some((r) => snapped >= toMin(r.start) && snapped < toMin(r.end));
    const blocked = cierresFor(dstr).some(
      (b) => b.all_day || (snapped >= toMin(b.start) && snapped < toMin(b.end)),
    );
    // Fuera de horario o bloqueado: solo admin (el backend validará con force).
    if ((!inWorking || blocked) && !isAdmin) return;
    onSlotClick({ date: dstr, start: toHHMM(snapped) });
  };

  const headerLabel = (dstr) => {
    const d = strToDate(dstr);
    const wd = d.toLocaleDateString('es-ES', { weekday: 'short' }).replace('.', '');
    return `${wd.charAt(0).toUpperCase()}${wd.slice(1)} ${d.getDate()}`;
  };

  if (cols.length === 0 || !agenda) return null;

  // Ancho mínimo de cada columna: con él, la agenda suelta (día o semana) se
  // desplaza de lado antes que estrujar las citas, como siempre. Con varias
  // agendas a la vez no hay mínimo (administración, 5/10/2026): las columnas
  // se reparten la pantalla y caben todas, sin scroll horizontal.
  const colMinWidth = ajustarAlAncho ? 0 : (compact ? 148 : 260);
  const conRegla = !sinRegla;

  return (
    <div
      className={`${ajustarAlAncho ? 'overflow-hidden' : 'overflow-x-auto'} rounded-xl border border-[#D9E3DD] bg-white`}
      data-testid="agenda-grid"
      data-ajustada={ajustarAlAncho ? '1' : undefined}
    >
      <div style={ajustarAlAncho ? undefined : { minWidth: GUTTER_PX + cols.length * colMinWidth }}>
        {showHeaders && (
          <div className="flex border-b border-[#D9E3DD] bg-white">
            {conRegla && <div className="shrink-0 sticky left-0 z-[25] bg-white" style={{ width: GUTTER_PX }} />}
            {cols.map((dstr) => (
              <button
                key={dstr}
                type="button"
                onClick={() => onDateHeaderClick?.(dstr)}
                data-testid={`agenda-col-header-${dstr}`}
                className={`flex-1 min-w-0 py-2 text-center text-xs font-medium border-l border-[#F0F6F2] transition-colors hover:bg-[rgba(46,110,94,0.08)] ${
                  dstr === today ? 'text-[#13231E]' : 'text-muted-foreground'
                }`}
                style={{ minWidth: colMinWidth }}
              >
                <span
                  className={
                    dstr === today
                      ? 'inline-flex items-center px-2 py-0.5 rounded-full bg-[#2E6E5E] text-white'
                      : undefined
                  }
                >
                  {headerLabel(dstr)}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="flex">
          {/* Gutter de horas (sticky en scroll horizontal). En una columna
              estrecha de «varias a la vez» no se pinta (5/10/2026). */}
          {conRegla && (
          <div
            className="shrink-0 sticky left-0 z-[25] bg-white border-r border-[#D9E3DD]"
            style={{ width: GUTTER_PX, height: gridHeight }}
            data-testid="agenda-regla-horas"
          >
            <div className="relative h-full">
              {stack ? (
                /* Apilado: la hora de CADA cita, alineada a su bloque (las horas
                   "se expanden" siguiendo a las citas; nunca desalineadas). */
                (laidOutByCol[cols[0]] || []).map((it) => (
                  <span
                    key={it.cita.id}
                    className="absolute right-1.5 tabular-nums text-[10px] font-medium text-[#13231E]/80"
                    style={{ top: it.top + 3 }}
                  >
                    {it.cita.start}
                  </span>
                ))
              ) : (
                ticks.map((m) => (
                  <span
                    key={m}
                    className={`absolute right-1.5 -translate-y-1/2 tabular-nums ${
                      m % 60 === 0 ? 'text-[10px] font-medium text-[#13231E]/80' : 'text-[9px] text-muted-foreground/70'
                    }`}
                    style={{ top: (m - minStart) * pxPerMin }}
                  >
                    {toHHMM(m)}
                  </span>
                ))
              )}
            </div>
          </div>
          )}

          {/* Columnas de día */}
          {cols.map((dstr) => {
            const laidOut = laidOutByCol[dstr] || [];
            const dayCierres = cierresFor(dstr);
            const dayAperturas = aperturasFor(dstr);
            const isToday = dstr === today;
            // Bloqueo de DÍA COMPLETO: manda sobre el aspecto de toda la
            // columna (rojo + candado). Los de tramo horario NO: esos siguen
            // pintándose solo en su franja (administración, 2026-09-17).
            const bloqueoDia = dayCierres.find((b) => b.all_day) || null;
            // El lateral solo se reserva si de verdad hay citas que enseñar:
            // un día bloqueado y vacío luce el rojo entero, sin recortes.
            const citasAlLateral = !!bloqueoDia && laidOut.length > 0;
            return (
              <div
                key={dstr}
                data-testid={`agenda-col-${dstr}`}
                data-bloqueada={bloqueoDia ? '1' : undefined}
                title={bloqueoDia ? `Agenda bloqueada: ${bloqueoDia.reason || 'sin motivo'}` : undefined}
                onClick={(e) => handleColumnClick(e, dstr)}
                className={`relative flex-1 min-w-0 border-l first:border-l-0 ${
                  bloqueoDia ? 'border-l-[#B91C1C]/40' : 'border-l-[#F0F6F2]'
                }`}
                style={{
                  minWidth: colMinWidth,
                  height: gridHeight,
                  // Día bloqueado entero: la columna va en rojo de arriba abajo
                  // (2026-09-17). Si no, apilado (lista de un día): fondo de
                  // tarjeta (blanco en claro, oscuro en modo oscuro);
                  // proporcional (semana): fondo gris rayado.
                  ...(bloqueoDia
                    ? { backgroundColor: hexToRgba(ROJO_BLOQUEO, 0.12) }
                    : stack
                    ? { backgroundColor: 'hsl(var(--card))' }
                    : {
                        backgroundColor: 'hsl(var(--muted))',
                        backgroundImage:
                          'repeating-linear-gradient(45deg, rgba(46,110,94,0.045) 0px, rgba(46,110,94,0.045) 5px, transparent 5px, transparent 11px)',
                      }),
                }}
              >
                {/* Franjas laborables en blanco (solo en proporcional/semana).
                    Si el día está bloqueado entero no se pintan: taparían de
                    blanco el rojo del bloqueo (2026-09-17). */}
                {!stack && !bloqueoDia && rangesFor(dstr).map((r, i) => (
                  <div
                    key={i}
                    className="absolute inset-x-0 bg-white"
                    style={{
                      top: (toMin(r.start) - minStart) * pxPerMin,
                      height: (toMin(r.end) - toMin(r.start)) * pxPerMin,
                    }}
                  />
                ))}

                {/* Aperturas extraordinarias: tinte verde suave + etiqueta.
                    La etiqueta lleva un «+» de texto y no el emoji de suma
                    (administración, 5/10/2026: los emojis no se ven bien). */}
                {dayAperturas.map((a) => (
                  <div
                    key={a.id}
                    className="absolute inset-x-0 z-[4] pointer-events-none border border-emerald-200 rounded-sm"
                    data-testid={`agenda-apertura-${a.id}`}
                    style={{
                      top: (toMin(a.start) - minStart) * pxPerMin,
                      height: (toMin(a.end) - toMin(a.start)) * pxPerMin,
                      backgroundColor: 'rgba(16,185,129,0.05)',
                    }}
                  >
                    <div className="px-1.5 py-0.5 text-[10px] text-emerald-700/90 truncate">
                      <span aria-hidden="true" className="font-bold">+</span> {a.reason || 'Huecos extra'}
                    </div>
                  </div>
                ))}

                {/* Líneas horizontales: en apilado, separan cada cita (alineadas
                    a los bloques); en proporcional, cada 30 min. */}
                {stack
                  ? laidOut.map((it) => (
                      <div
                        key={it.cita.id}
                        className="absolute inset-x-0 border-t border-[#F0F6F2]"
                        style={{ top: it.top }}
                      />
                    ))
                  : ticks.map((m) => (
                      <div
                        key={m}
                        className={`absolute inset-x-0 border-t ${m % 60 === 0 ? 'border-[#D9E3DD]' : 'border-[#F0F6F2]'}`}
                        style={{ top: (m - minStart) * pxPerMin }}
                      />
                    ))}

                {/* Cierres (encima del fondo, debajo de citas) */}
                {dayCierres.map((b) => (
                  <BloqueoBlock
                    key={b.id}
                    bloqueo={b}
                    minStart={minStart}
                    maxEnd={maxEnd}
                    pxPerMin={pxPerMin}
                    citasAlLateral={citasAlLateral}
                  />
                ))}

                {/* Ratos libres entre citas, a escala. El de mediodía se
                    etiqueta como Comida; el resto invita a citar en ese hueco.
                    Desde el 5/10/2026 la etiqueta es solo texto (sin el emoji
                    del plato, que no se veía bien) y nunca más ancha que su
                    columna: con varias agendas a la vez se corta con «…». */}
                {/* Con la agenda bloqueada el día entero no se ofrece ningún
                    rato libre: invitaría a citar justo donde no se puede
                    (administración, 2026-09-17: «no debe permitir citar nada»). */}
                {stack && !bloqueoDia && laidOut.filter((it) => it.huecoArriba).map((it) => {
                  const g = it.huecoArriba;
                  const esComida = g.tipo === 'comida';
                  return (
                    <div
                      key={`hueco-${it.cita.id}`}
                      className="absolute inset-x-0 z-[6] flex items-center justify-center"
                      style={{ top: it.top - g.px, height: g.px }}
                      data-testid={esComida ? 'agenda-comida-gap' : 'agenda-hueco-libre'}
                    >
                      {esComida ? (
                        <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full text-[11px] font-semibold text-[#C97D1E] bg-[#FBF3E7] px-2.5 py-0.5 rounded-full border border-[#EBD7B5] pointer-events-none">
                          <span className="truncate">Comida · {duracionLegible(g.minutos)}</span>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onSlotClick?.({ date: dstr, start: toHHMM(g.desde) }); }}
                          title={`Libre de ${toHHMM(g.desde)} a ${toHHMM(g.hasta)} — pulsa para citar`}
                          className="w-full h-full flex items-center justify-center group"
                        >
                          <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full text-[11px] font-medium text-muted-foreground bg-transparent px-2 py-0.5 rounded-full border border-dashed border-border group-hover:border-[#2E6E5E] group-hover:text-[#2E6E5E] transition-colors">
                            <span className="truncate">{duracionLegible(g.minutos)} libres</span>
                          </span>
                        </button>
                      )}
                    </div>
                  );
                })}

                {/* Citas */}
                {laidOut.map((item) => (
                  <CitaBlock
                    key={item.cita.id}
                    item={item}
                    pxPerMin={pxPerMin}
                    minStart={minStart}
                    color={conceptColor(conceptByCode[item.cita.concept_code])}
                    onCitaClick={onCitaClick}
                    consultaConjunta={consultaConjuntaDeLaCita(item.cita, [agenda, ...agendas])}
                    esQuirofano={(agenda?.tipo || agenda?.type) === 'quirofano'}
                    bloqueada={!!bloqueoDia}
                    compacta={compact}
                    ajustada={ajustarAlAncho}
                    estrecha={ajustarAlAncho && sinRegla}
                  />
                ))}

                {/* Línea "ahora" (solo en proporcional; en apilado no aplica) */}
                {!stack && isToday && nowMin >= minStart && nowMin <= maxEnd && (
                  <div
                    className="absolute inset-x-0 z-[20] pointer-events-none"
                    data-testid="agenda-now-line"
                    style={{ top: (nowMin - minStart) * pxPerMin }}
                  >
                    <div className="border-t border-red-500" />
                    <div className="absolute -left-[3px] -top-[3px] w-1.5 h-1.5 rounded-full bg-red-500" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
